import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CButton,
  CFormInput,
  CFormCheck,
  CFormSwitch,
  CInputGroup,
  CInputGroupText,
  CAlert,
  CBadge,
  CTabContent,
  CTabPane,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilChevronBottom,
  cilGlobeAlt,
  cilLockLocked,
  cilCreditCard,
  cilEnvelopeClosed,
  cilPeople,
  cilCheckCircle,
  cilCheck,
  cilCheckAlt,
  cilClock,
  cilBell,
  cilCalendarCheck,
  cilLink,
  cilCopy,
  cilReload,
  cilInfo,
  cilBolt,
  cilTask,
  cilDescription,
  cilCog,
  cilWarning,
  cilUserPlus,
} from '@coreui/icons'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import ToastComponent from 'src/components/common/TaostComponent'
import CommonService from 'src/service/CommonService'
import GameEntryService from 'src/service/GameEntryService'
import MemberCode from '../User/MemberCode'
import GameGuestInvitations from '../User/GameGuestInvitations'
import MemberInvites from '../Player/MemberInvites'
import StepperBar from '../SetupProgress/StepperBar'

const PRIVACY_TO_LABEL = { 1: 'public', 2: 'private' }
const LABEL_TO_PRIVACY = { public: 1, private: 2 }

// entry_requirement is a single 0-5 code that already encodes visibility + join method together:
// Public  -> 0 = Instant Join, 1 = Complete Verification Later, 2 = Complete Verification Before Joining
// Private -> 3 = Game Code (required), 4 = Game Code + Verification Later, 5 = Game Code + Verification Before Joining
const PUBLIC_LABEL_TO_ENTRY_REQ = { none: 0, optional: 1, required: 2 }
const PUBLIC_ENTRY_REQ_TO_LABEL = { 0: 'none', 1: 'optional', 2: 'required' }
const PRIVATE_LABEL_TO_ENTRY_REQ = { none: 3, game_code_optional: 4, game_code_required: 5 }
const PRIVATE_ENTRY_REQ_TO_LABEL = { 3: 'none', 4: 'game_code_optional', 5: 'game_code_required' }

const labelToEntryReq = (visibilityLabel, entryRequirementLabel) =>
  (visibilityLabel === 'private' ? PRIVATE_LABEL_TO_ENTRY_REQ : PUBLIC_LABEL_TO_ENTRY_REQ)[
    entryRequirementLabel
  ]
const entryReqToLabel = (visibilityLabel, code) =>
  (visibilityLabel === 'private' ? PRIVATE_ENTRY_REQ_TO_LABEL : PUBLIC_ENTRY_REQ_TO_LABEL)[code]
const CONFIRMATION_TO_LABEL = { 1: 'admin_approval', 2: 'automatic' }
const LABEL_TO_CONFIRMATION = { admin_approval: 1, automatic: 2 }

const NOTIF_TO_LABEL = { 0: 'off', 1: 'instant', 2: 'daily' }
const LABEL_TO_NOTIF = { off: 0, instant: 1, daily: 2 }
const NOTIF_LABEL = { off: 'Off', instant: 'Instant Email', daily: 'Daily Summary' }

const STEP_LABELS = ['Visibility', 'Notifications', 'Invitation']

const STEP_ICONS = [cilGlobeAlt, cilBell, cilEnvelopeClosed]

const SECTION_TITLES = ['Game Visibility', 'Notifications', 'Invitation']

const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, '').trim()

const SelectableCard = ({
  selected,
  onClick,
  icon,
  title,
  description,
  disabled,
  badge,
  indicator,
}) => (
  <div
    className={`border rounded-4 p-3 h-100 position-relative ${
      selected ? 'border-primary border-2' : 'border-secondary-subtle'
    }`}
    style={{
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 1 : 1,
      backgroundColor: selected ? 'rgba(var(--cui-primary-rgb), 0.06)' : undefined,
      boxShadow: selected ? '0 0 0 3px rgba(var(--cui-primary-rgb), 0.1)' : 'none',
      transition: 'box-shadow .15s ease, border-color .15s ease, background-color .15s ease',
    }}
    onClick={disabled ? undefined : onClick}
  >
    {indicator === 'radio' && (
      <div
        className={`position-absolute d-flex align-items-center justify-content-center rounded-circle border ${
          selected ? 'border-primary' : 'border-secondary-subtle'
        }`}
        style={{ width: 18, height: 18, top: 12, right: 12 }}
      >
        {selected && <div className="rounded-circle bg-primary" style={{ width: 9, height: 9 }} />}
      </div>
    )}
    <div className="d-flex align-items-center gap-2 mb-1">
      {indicator === 'checkbox' && (
        <div
          className={`d-flex align-items-center justify-content-center rounded-1 border flex-shrink-0 ${
            selected ? 'bg-primary border-primary' : 'border-secondary-subtle'
          }`}
          style={{ width: 18, height: 18 }}
        >
          {selected && (
            <CIcon icon={cilCheck} className="text-white" style={{ width: 12, height: 12 }} />
          )}
        </div>
      )}
      {icon && (
        <div
          className={`d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 ${
            selected ? 'bg-primary text-white' : 'bg-body-secondary text-primary'
          }`}
          style={{ width: 34, height: 34, transition: 'background-color .15s ease' }}
        >
          <CIcon icon={icon} size="lg" />
        </div>
      )}
      <div className="fw-semibold">{title}</div>
      {badge}
    </div>
    <div className="small">{description}</div>
  </div>
)

const InfoBox = ({ children, color = 'info', icon = cilInfo, className = '' }) => (
  <CAlert color={color} className={`d-flex align-items-start gap-2 border-0 ${className}`}>
    <CIcon icon={icon} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

const StepperNav = ({ steps, current, onStepClick }) => {
  const total = steps.length
  const trackInset = (0.5 / total) * 100
  const fillPct = ((current - 1) / total) * 100
  // With few steps, justify-content-between spreads nodes across the full card width and
  // leaves an oversized gap — cap the row so nodes stay close together instead.
  const rowMaxWidth = total <= 2 ? 420 : undefined

  return (
    <>
      <CCard className="mb-4 border-0 shadow-sm">
        <CCardHeader
          className="d-flex justify-content-between align-items-center border-0 fw-semibold"
          style={{
            backgroundColor: 'var(--cui-accordion-active-bg)',
            color: 'var(--cui-accordion-active-color)',
          }}
        >
          {/* <span>Game Entry Setup</span> */}
          {/* <div className="d-flex align-items-center gap-3">
            <span className="badge rounded-pill bg-body text-primary px-3 py-1">
              Step {current} of {total}
            </span>
            <CIcon icon={cilChevronBottom} />
          </div> */}
        </CCardHeader>
        <CCardBody>
          <div
            className="position-relative"
            style={{ maxWidth: rowMaxWidth, margin: rowMaxWidth ? '0 auto' : undefined }}
          >
            <div
              className="position-absolute bg-secondary-subtle d-none d-md-block"
              style={{
                top: 20,
                left: `${trackInset}%`,
                right: `${trackInset}%`,
                height: 3,
                borderRadius: 2,
              }}
            />
            <div
              className="position-absolute bg-success d-none d-md-block"
              style={{
                top: 20,
                left: `${trackInset}%`,
                width: `${fillPct}%`,
                height: 3,
                borderRadius: 2,
                transition: 'width .25s ease',
              }}
            />
            <div className="d-flex justify-content-between flex-wrap gap-3">
              {steps.map((s) => {
                const done = s.n < current
                const active = s.n === current
                const clickable = true
                return (
                  <div
                    key={s.n}
                    className="d-flex flex-column align-items-center text-center position-relative"
                    style={{
                      minWidth: 100,
                      flex: '1 1 0',
                      cursor: clickable ? 'pointer' : 'default',
                      opacity: clickable ? 1 : 0.45,
                      zIndex: 1,
                    }}
                    onClick={() => clickable && onStepClick(s.n)}
                  >
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 mb-2 ${
                        done
                          ? 'bg-success text-white'
                          : active
                          ? 'bg-primary text-white'
                          : 'bg-body border text-medium-emphasis'
                      }`}
                      style={{
                        width: 40,
                        height: 40,
                        boxShadow: active ? '0 0 0 4px rgba(var(--cui-primary-rgb), 0.15)' : 'none',
                        transition: 'box-shadow .2s ease, background-color .2s ease',
                      }}
                    >
                      <CIcon icon={done ? cilCheckAlt : STEP_ICONS[s.n - 1]} size="lg" />
                    </div>
                    <div className={`small ${active ? 'fw-semibold text-primary' : 'fw-medium'}`}>
                      {s.label}
                    </div>
                    <div className="text-medium-emphasis" style={{ fontSize: 12 }}>
                      {s.value}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

const GameEntrySettings = () => {
  const navigate = useNavigate()

  const [step, setStep] = useState(1)
  const [maxStepReached, setMaxStepReached] = useState(1)

  const [visibility, setVisibility] = useState('public')
  const [gameCode, setGameCode] = useState('DDCC2026')
  const [invitationTab, setInvitationTab] = useState('import_guest')
  // Mirrors the Import List wizard's Validate/Review stats, shown in the right sidebar's
  // "Import Summary" card so it isn't duplicated inside the wizard's own (narrower) column.
  const [importSummary, setImportSummary] = useState(null)
  // Live count of rows in the "Add People" form, shown in the sidebar's "Invitation Summary" card.
  const [addPeopleCount, setAddPeopleCount] = useState(null)
  // Bumped after an import/add completes so the shared "Invitations" table (below the tabs)
  // refetches, since it isn't the same component instance that just added the guests.
  const [invitationsReloadToken, setInvitationsReloadToken] = useState(0)

  const [entryRequirement, setEntryRequirement] = useState('optional')

  const [reqTitle, setReqTitle] = useState('Club Membership Confirmation')
  const [instructions, setInstructions] = useState(
    "Please confirm your membership by completing the form using the link below and submit your details. Once done, click 'Mark as Done'.",
  )
  const [supportingLink, setSupportingLink] = useState('')
  const [openInNewTab, setOpenInNewTab] = useState(true)
  const [confirmFields, setConfirmFields] = useState({
    reference: false,
    note: false,
    screenshot: false,
  })

  const [confirmationType, setConfirmationType] = useState('admin_approval')
  const [approvalNotificationType, setApprovalNotificationType] = useState('off')
  const [newMemberNotificationType, setNewMemberNotificationType] = useState('off')

  const [saving, setSaving] = useState(false)
  const [isTeamExists, setIsTeamExists] = useState(false)

  useEffect(() => {
    CommonService.gameStructureInfo()
      .then((res) => {
        if (res.status === 200) setIsTeamExists(!!res.is_team_exists)
      })
      .catch(() => {
        // Keep the default (unlocked) if this can't be loaded.
      })

    GameEntryService.getEntrySettings()
      .then((res) => {
        if (res.status !== 200 || !res.data) return
        const s = res.data
        const visibilityLabelFromApi = s.game_privacy
          ? PRIVACY_TO_LABEL[s.game_privacy] || 'public'
          : 'public'
        if (s.game_privacy) setVisibility(visibilityLabelFromApi)
        if (s.game_accept_code) setGameCode(s.game_accept_code)
        if (s.entry_requirement !== undefined && s.entry_requirement !== null) {
          setEntryRequirement(
            entryReqToLabel(visibilityLabelFromApi, s.entry_requirement) ?? 'optional',
          )
        }
        if (s.requirement_title) setReqTitle(s.requirement_title)
        if (s.instructions) setInstructions(s.instructions)
        if (s.supporting_link) setSupportingLink(s.supporting_link)
        setOpenInNewTab(!!s.open_in_new_tab)
        setConfirmFields({
          reference: !!s.confirm_reference,
          note: !!s.confirm_note,
          screenshot: !!s.confirm_screenshot,
        })
        if (s.confirmation_type) {
          setConfirmationType(CONFIRMATION_TO_LABEL[s.confirmation_type] || 'admin_approval')
        }
        if (s.approval_notification_type !== undefined && s.approval_notification_type !== null) {
          setApprovalNotificationType(NOTIF_TO_LABEL[s.approval_notification_type] || 'off')
        }
        if (
          s.new_member_notification_type !== undefined &&
          s.new_member_notification_type !== null
        ) {
          setNewMemberNotificationType(NOTIF_TO_LABEL[s.new_member_notification_type] || 'off')
        }
      })
      .catch(() => {
        // Keep the defaults above if settings can't be loaded (e.g. a brand new game).
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const skipAdditionalSteps = entryRequirement === 'none'
  const requiresGameCode =
    visibility === 'private' &&
    (entryRequirement === 'none' ||
      entryRequirement === 'game_code_required' ||
      entryRequirement === 'game_code_optional')
  const isOptionalFlavor =
    entryRequirement === 'optional' || entryRequirement === 'game_code_optional'
  const isRequiredFlavor =
    entryRequirement === 'required' || entryRequirement === 'game_code_required'

  const visibilityLabel = visibility === 'public' ? 'Public' : 'Private'
  const entryRequirementLabel = {
    none: visibility === 'private' ? 'Game Code' : 'Instant Join',
    optional: 'Complete Verification Later',
    required: 'Complete Verification Before Joining',
    game_code_optional: 'Game Code + Complete Verification Later',
    game_code_required: 'Game Code + Complete Verification Before Joining',
  }[entryRequirement]
  const confirmationLabel =
    confirmationType === 'admin_approval' ? 'Admin Approval' : 'Automatically Approved'
  // Approval Requests notifications only apply when there's something to approve; New Members
  // Joined always applies, so the summary shows just that half when Approval is hidden.
  const hideApprovalNotifications = skipAdditionalSteps || confirmationType === 'automatic'
  const notificationLabel = hideApprovalNotifications
    ? `New Members: ${NOTIF_LABEL[newMemberNotificationType]}`
    : `Approval: ${NOTIF_LABEL[approvalNotificationType]} · New Members: ${NOTIF_LABEL[newMemberNotificationType]}`
  const membersProvideLabel = Object.entries({
    reference: 'Reference Number',
    note: 'Note',
    screenshot: 'Screenshot',
  })
    .filter(([key]) => confirmFields[key])
    .map(([, label]) => label)
    .join(', ')

  // Notifications step stays reachable even when entry requires no verification, since "New
  // Members Joined" applies to every join method — only the Approval Requests half (gated by
  // hideApprovalNotifications above) is conditional on there being something to approve.
  const activeSteps = [1, 2, 3]

  const stepMeta = [
    { n: 1, label: STEP_LABELS[0], value: visibilityLabel },
    { n: 2, label: STEP_LABELS[1], value: notificationLabel },
    { n: 3, label: STEP_LABELS[2], value: 'Guests & Invites' },
  ].filter((s) => activeSteps.includes(s.n))

  const sectionSubtitles = [
    'Who can see this game?',
    'Choose how you want to be notified about member submissions.',
    'Manage game guests and member invitations.',
  ]

  const saveCurrentStep = async () => {
    try {
      if (step === 1) {
        const res = await GameEntryService.updateVisibility({
          privacy: LABEL_TO_PRIVACY[visibility],
        })
        if (res.status !== 200) throw new Error(res.message)
        if (res.data?.game_accept_code) setGameCode(res.data.game_accept_code)

        const reqRes = await GameEntryService.updateEntryRequirement({
          entry_requirement: labelToEntryReq(visibility, entryRequirement),
        })
        if (reqRes.status !== 200) throw new Error(reqRes.message)

        if (!skipAdditionalSteps) {
          const setupRes = await GameEntryService.updateAdditionalSetup({
            requirement_title: reqTitle,
            instructions,
            supporting_link: supportingLink,
            open_in_new_tab: openInNewTab,
            confirm_reference: confirmFields.reference,
            confirm_note: confirmFields.note,
            confirm_screenshot: confirmFields.screenshot,
          })
          if (setupRes.status !== 200) throw new Error(setupRes.message)

          const confirmRes = await GameEntryService.updateConfirmation({
            confirmation_type: LABEL_TO_CONFIRMATION[confirmationType],
            confirm_reference: confirmFields.reference,
            confirm_note: confirmFields.note,
          })
          if (confirmRes.status !== 200) throw new Error(confirmRes.message)
        }
      } else if (step === 2) {
        const res = await GameEntryService.updateNotificationSettings({
          approval_notification_type: LABEL_TO_NOTIF[approvalNotificationType],
          new_member_notification_type: LABEL_TO_NOTIF[newMemberNotificationType],
        })
        if (res.status !== 200) throw new Error(res.message)
      } else if (step === 3) {
        // Guests can always be imported/created and invited from this step, so the game
        // always supports both joining methods (1 = Game Code, 2 = Member Invite, 3 = Both).
        const res = await GameEntryService.updateJoiningMethod({ joining_method: 3 })
        if (res.status !== 200) throw new Error(res.message)
      }
      return true
    } catch (e) {
      ToastComponent(e?.message || 'Something went wrong while saving this step.', 'error')
      return false
    }
  }

  const handleUpdate = async () => {
    setSaving(true)
    const ok = await saveCurrentStep()
    setSaving(false)
    if (ok) ToastComponent('Changes updated successfully.', 'success')
  }

  const handleNext = () => {
    const idx = activeSteps.indexOf(step)
    if (idx === -1 || idx === activeSteps.length - 1) return
    const next = activeSteps[idx + 1]
    setStep(next)
    setMaxStepReached((m) => Math.max(m, next))
  }

  const goBack = () => {
    const idx = activeSteps.indexOf(step)
    if (idx <= 0) return
    setStep(activeSteps[idx - 1])
  }

  const goToStep = (n) => {
    if (!activeSteps.includes(n)) return
    setStep(n)
    setMaxStepReached((m) => Math.max(m, n))
  }

  const journeySteps = [
    {
      icon: cilPeople,
      title: 'Discover Game',
      desc:
        visibility === 'public'
          ? 'Members see the game in the hub.'
          : 'Members find the game via invite or code.',
    },
    {
      icon: cilCreditCard,
      title: 'Enter Game Code or Accept Invite',
      desc: 'Members join using a valid game code or a member invitation.',
    },
  ]
  journeySteps.push({
    icon: cilCheckCircle,
    title: 'Join the Game',
    desc: 'Members join the game immediately.',
  })
  if (!skipAdditionalSteps) {
    journeySteps.push({
      icon: cilTask,
      title: `Complete Additional Step${isOptionalFlavor ? ' (Optional)' : ''}`,
      desc: isOptionalFlavor
        ? 'Members can complete this additional step anytime.'
        : 'Members must complete and be approved before joining.',
    })
  }

  const summaryRows = [
    { icon: cilGlobeAlt, label: 'Visibility', value: visibilityLabel },
    { icon: cilEnvelopeClosed, label: 'Entry Requirement', value: entryRequirementLabel },
  ]
  if (!skipAdditionalSteps) {
    summaryRows.push({
      icon: cilCheckCircle,
      label: 'Confirmation',
      value: maxStepReached >= 2 ? confirmationLabel : '—',
    })
    summaryRows.push({
      icon: cilTask,
      label: 'Members Provide',
      value: maxStepReached >= 2 ? membersProvideLabel || '—' : '—',
    })
    summaryRows.push({
      icon: cilDescription,
      label: 'Instructions',
      value: maxStepReached >= 2 ? (stripHtml(instructions) ? 'Set' : '—') : '—',
    })
  }
  summaryRows.push({
    icon: cilBell,
    label: 'Notifications',
    value: maxStepReached >= 2 ? notificationLabel : '—',
  })

  const renderVisibility = () => (
    <>
      <CRow className="g-3 mb-3">
        <CCol md={6}>
          <SelectableCard
            indicator="radio"
            icon={cilGlobeAlt}
            title="Public"
            description="Anyone in the hub can discover and join this game."
            selected={visibility === 'public'}
            onClick={() => {
              setVisibility('public')
              // "Game Code + …" combos only make sense for private games — fall back to the
              // plain optional/required equivalent so Public never persists a code-gated value.
              setEntryRequirement((r) =>
                r === 'game_code_optional'
                  ? 'optional'
                  : r === 'game_code_required'
                  ? 'required'
                  : r,
              )
            }}
          />
        </CCol>
        <CCol md={6}>
          <SelectableCard
            indicator="radio"
            icon={cilLockLocked}
            title="Private"
            description="Only invited members or users with a valid code can enter."
            selected={visibility === 'private'}
            onClick={() => setVisibility('private')}
          />
        </CCol>
      </CRow>
      {visibility === 'private' && (
        <InfoBox>
          Private games are hidden from the hub. Members can only join using a valid code or an
          invite.
        </InfoBox>
      )}
      <CCard className="border-secondary-subtle rounded-4 mt-4">
        <CCardBody>
          <div className="fw-semibold fs-6 mb-1">How do members join?</div>
          <div className="small mb-3">Select the method by which members can join the game.</div>
          {renderEntryRequirement()}
        </CCardBody>
      </CCard>
      {requiresGameCode && <div className="mt-4">{renderGameCodeBox()}</div>}
      {entryRequirement !== 'none' && (
        <CCard className="border-secondary-subtle rounded-4 mt-4">
          <CCardBody>{renderEntryRequirementDetails()}</CCardBody>
        </CCard>
      )}
      {!skipAdditionalSteps && (
        <>
          <CCard className="border-secondary-subtle rounded-4 mt-4">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Verification Requirements</div>
              <div className="small mb-3">Define the additional step members need to complete.</div>
              {renderAdditionalSetup()}
            </CCardBody>
          </CCard>
          <CCard className="border-secondary-subtle rounded-4 mt-4">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Require Additional Information?</div>
              <div className="small mb-3">
                Enable members to provide additional information during the entry process.
              </div>
              {renderMemberConfirmationFields()}
            </CCardBody>
          </CCard>
          <CCard className="border-secondary-subtle rounded-4 mt-4">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Game Entry Approval Method</div>
              <div className="small mb-3">How should member game entry requests be approved?</div>
              {renderConfirmation()}
            </CCardBody>
          </CCard>
        </>
      )}
    </>
  )

  const handleRegenerateCode = async () => {
    const code = `MCT${Math.random().toString(36).slice(2, 8).toUpperCase()}`
    setGameCode(code)
    try {
      const res = await GameEntryService.updateVisibility({
        privacy: LABEL_TO_PRIVACY[visibility] || 2,
        game_accept_code: code,
      })
      if (res.status === 200) ToastComponent('Game code regenerated.', 'success')
    } catch (e) {
      ToastComponent('Could not regenerate the game code.', 'error')
    }
  }

  const handleCodeBlur = async () => {
    if (!gameCode) return
    try {
      const res = await GameEntryService.updateVisibility({
        privacy: LABEL_TO_PRIVACY[visibility] || 2,
        game_accept_code: gameCode,
      })
      if (res.status === 200) ToastComponent('Game code saved.', 'success')
    } catch (e) {
      ToastComponent('Could not save the game code.', 'error')
    }
  }

  const renderGameCodeBox = () => (
    <CCard className="border-secondary-subtle rounded-4">
      <CCardBody>
        <CRow className="g-3 align-items-start">
          <CCol md={7}>
            <div className="fw-semibold mb-1">Game Code (Single code for this game)</div>
            <div className="small mb-3">
              Create and manage a single code that members will use to access this game.
            </div>
            <CInputGroup>
              <CFormInput
                value={gameCode}
                onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                onBlur={handleCodeBlur}
                className="fw-semibold text-primary"
              />
              <CInputGroupText
                role="button"
                onClick={() => {
                  navigator.clipboard?.writeText(gameCode)
                  ToastComponent('Game code copied to clipboard.', 'success')
                }}
              >
                <CIcon icon={cilCopy} />
              </CInputGroupText>
              <CButton color="secondary" variant="outline" onClick={handleRegenerateCode}>
                <CIcon icon={cilReload} className="me-1" />
                Regenerate Code
              </CButton>
            </CInputGroup>
            <div className="small mt-2">
              Share this code with your members. You can regenerate it anytime.
            </div>
          </CCol>
          <CCol md={5}>
            <InfoBox color="light" icon={cilInfo}>
              <div className="fw-semibold mb-1">How it works</div>
              <ul className="ps-3 mb-0">
                <li>Members enter the code on the game page</li>
                <li>If the code is valid, they can access the game</li>
                <li>They can then complete any requirements (if set) before joining</li>
              </ul>
            </InfoBox>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  )

  const INVITATION_TABS = [
    {
      key: 'import_guest',
      icon: cilDescription,
      label: 'Import List',
      description: 'Upload a CSV or Excel file',
    },
    {
      key: 'create_list',
      icon: cilUserPlus,
      label: 'Add People',
      description: 'Add someone using their details',
    },
    {
      key: 'member_invite',
      icon: cilPeople,
      label: 'Previous Members',
      description: 'Invite members from another game',
    },
  ]

  const InvitationTabCard = ({ selected, onClick, icon, title, description }) => (
    <div
      className={`border rounded-4 p-3 h-100 ${
        selected ? 'border-success border-2' : 'border-secondary-subtle'
      }`}
      style={{
        cursor: 'pointer',
        backgroundColor: selected ? 'rgba(25,135,84,0.06)' : undefined,
        boxShadow: selected ? '0 0 0 3px rgba(25,135,84,0.1)' : 'none',
        transition: 'box-shadow .15s ease, border-color .15s ease, background-color .15s ease',
      }}
      onClick={onClick}
    >
      <div className="d-flex align-items-center gap-2 mb-1">
        <div
          className={`d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 ${
            selected ? 'bg-success text-white' : 'bg-body-secondary text-success'
          }`}
          style={{ width: 40, height: 40, transition: 'background-color .15s ease' }}
        >
          <CIcon icon={icon} size="lg" />
        </div>
        <div className="fw-semibold">{title}</div>
      </div>
      <div className="small text-medium-emphasis">{description}</div>
    </div>
  )

  const renderInvitation = () => (
    <>
      <div>
        <CRow className="g-3 mb-4">
          {INVITATION_TABS.map((tab) => (
            <CCol md={4} key={tab.key}>
              <InvitationTabCard
                selected={invitationTab === tab.key}
                onClick={() => setInvitationTab(tab.key)}
                icon={tab.icon}
                title={tab.label}
                description={tab.description}
              />
            </CCol>
          ))}
        </CRow>
        <CTabContent>
          <CTabPane visible={invitationTab === 'import_guest'}>
            <MemberCode
              section="import"
              onImportSummaryChange={setImportSummary}
              onGuestsChanged={() => setInvitationsReloadToken((t) => t + 1)}
            />
          </CTabPane>
          <CTabPane visible={invitationTab === 'create_list'}>
            <MemberCode
              section="createList"
              visibility={visibility}
              onAddPeopleSummaryChange={setAddPeopleCount}
              onGuestsChanged={() => setInvitationsReloadToken((t) => t + 1)}
            />
          </CTabPane>
          <CTabPane visible={invitationTab === 'member_invite'}>
            <MemberInvites />
          </CTabPane>
        </CTabContent>
        {/* Previous Members brings its own member list, with its own invite state and Send /
            Re-send buttons - the shared game-guest Invitations table is noise under it. */}
        {invitationTab !== 'member_invite' && (
          <div className="mt-4">
            <GameGuestInvitations reloadToken={invitationsReloadToken} />
          </div>
        )}
      </div>
    </>
  )

  const renderEntryRequirement = () => {
    return (
      <>
        <CRow className="g-3 mb-3">
          {visibility === 'private' ? (
            <>
              <CCol md={6}>
                <SelectableCard
                  indicator="radio"
                  icon={cilCreditCard}
                  title="Game Code"
                  description="Members enter a valid game code to access this game."
                  selected={entryRequirement === 'none'}
                  onClick={() => setEntryRequirement('none')}
                />
              </CCol>
              <CCol md={6}>
                <SelectableCard
                  indicator="radio"
                  icon={cilCreditCard}
                  title="Game Code + Complete Verification Later"
                  description="Members enter a game code, and can complete this step anytime."
                  selected={entryRequirement === 'game_code_optional'}
                  onClick={() => setEntryRequirement('game_code_optional')}
                />
              </CCol>
              <CCol md={6}>
                <SelectableCard
                  indicator="radio"
                  icon={cilCreditCard}
                  title="Game Code + Complete Verification Before Joining"
                  description="Members enter a game code, and must complete/be approved before joining."
                  selected={entryRequirement === 'game_code_required'}
                  onClick={() => setEntryRequirement('game_code_required')}
                />
              </CCol>
            </>
          ) : (
            <>
              <CCol md={4}>
                <SelectableCard
                  indicator="radio"
                  icon={cilCheckAlt}
                  title="Instant Join"
                  description="No additional setup required. Members can discover the game and join immediately."
                  selected={entryRequirement === 'none'}
                  onClick={() => setEntryRequirement('none')}
                />
              </CCol>
              <CCol md={4}>
                <SelectableCard
                  indicator="radio"
                  icon={cilClock}
                  title="Complete Verification Later"
                  description="Members can join now and complete the verification requirement from their game dasboard later at any time."
                  selected={entryRequirement === 'optional'}
                  onClick={() => setEntryRequirement('optional')}
                />
              </CCol>
              <CCol md={4}>
                <SelectableCard
                  indicator="radio"
                  icon={cilLockLocked}
                  title="Complete Verification Before Joining"
                  description="Members must complete the verification requirement before they are allowed into the game."
                  selected={entryRequirement === 'required'}
                  onClick={() => setEntryRequirement('required')}
                />
              </CCol>
            </>
          )}
        </CRow>
        <div className="mt-3">
          <InfoBox color="light" icon={cilInfo}>
            Click &ldquo;Save &amp; Continue&rdquo; to set up instructions and confirmation
            preferences.
          </InfoBox>
        </div>
      </>
    )
  }

  const renderEntryRequirementDetails = () => (
    <>
      <InfoBox className="mb-3">
        {isOptionalFlavor
          ? 'Members will be able to join the game and complete this step anytime.'
          : 'Members must complete and be approved for this step before they can join the game.'}
      </InfoBox>
      <div className="fw-semibold text-primary mb-2">What happens next?</div>
      <ul className="ps-3 mb-0">
        <li>
          {isOptionalFlavor
            ? 'Members can join the game immediately.'
            : 'Members must complete this step before they can join.'}
        </li>
        <li>They can complete this additional step from their team dashboard.</li>
        <li>
          You can review and track completions (if Admin Approval is selected on the next step).
        </li>
      </ul>
    </>
  )

  const renderAdditionalSetup = () => (
    <>
      <CRow className="g-4">
        <CCol md={6}>
          <label className="form-label fw-semibold d-flex justify-content-between">
            <span>Requirement Title</span>
            <span className="fw-normal">{reqTitle.length}/60</span>
          </label>
          <div className="small mb-1">A short title for this requirement (visible to members).</div>
          <CFormInput
            value={reqTitle}
            maxLength={60}
            onChange={(e) => setReqTitle(e.target.value)}
          />
        </CCol>
        <CCol md={6}>
          <label className="form-label fw-semibold">Supporting Link (Optional)</label>
          <div className="small mb-1">Add a link to a form, website or instruction page.</div>
          <CInputGroup>
            <CInputGroupText>
              <CIcon icon={cilLink} />
            </CInputGroupText>
            <CFormInput
              placeholder="https://"
              value={supportingLink}
              onChange={(e) => setSupportingLink(e.target.value)}
            />
          </CInputGroup>
          <CFormCheck
            className="mt-2"
            label="Open link in new tab"
            checked={openInNewTab}
            onChange={(e) => setOpenInNewTab(e.target.checked)}
          />
        </CCol>
        <CCol md={12}>
          <label className="form-label fw-semibold d-flex justify-content-between">
            <span>Instructions for Members</span>
            <span className="fw-normal">{stripHtml(instructions).length}/1000</span>
          </label>
          <div className="small mb-1">Explain clearly what members need to do.</div>
          <ReactQuill
            theme="snow"
            value={instructions}
            onChange={setInstructions}
            modules={{
              toolbar: [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link'],
              ],
            }}
          />
        </CCol>
      </CRow>
    </>
  )

  const renderMemberConfirmationFields = () => (
    <>
      <CRow className="g-3">
        <CCol md={4}>
          <SelectableCard
            indicator="checkbox"
            title="Reference / Confirmation Number"
            description="Members enter a reference or confirmation number."
            selected={confirmFields.reference}
            onClick={() => setConfirmFields((f) => ({ ...f, reference: !f.reference }))}
          />
        </CCol>
        <CCol md={4}>
          <SelectableCard
            indicator="checkbox"
            title="Note"
            description="Members enter a short note or additional information."
            selected={confirmFields.note}
            onClick={() => setConfirmFields((f) => ({ ...f, note: !f.note }))}
          />
        </CCol>
        <CCol md={4}>
          <SelectableCard
            indicator="checkbox"
            title="Screenshot Upload"
            description="Members upload a screenshot as proof."
            badge={
              <CBadge color="info" shape="rounded-pill" className="ms-1">
                Coming Soon
              </CBadge>
            }
            selected={false}
            disabled
          />
        </CCol>
      </CRow>
      <div className="mt-3">
        <InfoBox color="light" icon={cilInfo}>
          Selected fields will be shown to members when they mark this requirement as complete.
        </InfoBox>
      </div>
    </>
  )

  const renderConfirmation = () => (
    <>
      <CRow className="g-3 mb-3">
        <CCol md={6}>
          <SelectableCard
            indicator="radio"
            icon={cilPeople}
            title="Admin Approval"
            description="Member requests are reviewed, rejected or approved by the game admin before they can join."
            selected={confirmationType === 'admin_approval'}
            onClick={() => setConfirmationType('admin_approval')}
          />
        </CCol>
        <CCol md={6}>
          <SelectableCard
            indicator="radio"
            icon={cilBolt}
            title="Automatically Approved"
            description="Member requests are approved instantly upon submission of verification request."
            selected={confirmationType === 'automatic'}
            onClick={() => setConfirmationType('automatic')}
          />
        </CCol>
      </CRow>
      {isRequiredFlavor && confirmationType === 'automatic' && (
        <InfoBox className="mb-3">
          <strong>Important:</strong> Since this is Required Before Joining, members will be able to
          join the game immediately after marking this step as complete.
        </InfoBox>
      )}
      {confirmationType === 'admin_approval' && (
        <CCard className="border-secondary-subtle rounded-4 mt-3">
          <CCardBody>
            <div className="fw-semibold mb-1">Information to Review (for Admin Approval)</div>
            <div className="small mb-3">
              Admins will see the following information from members.
            </div>
            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
              <div>
                <div className="fw-semibold">Reference / Confirmation Number</div>
                <div className="small">Members enter a reference or confirmation number.</div>
              </div>
              <CFormSwitch
                checked={confirmFields.reference}
                onChange={(e) => setConfirmFields((f) => ({ ...f, reference: e.target.checked }))}
              />
            </div>
            <div className="d-flex justify-content-between align-items-center py-2">
              <div>
                <div className="fw-semibold">Note</div>
                <div className="small">Members enter a short note or additional information.</div>
              </div>
              <CFormSwitch
                checked={confirmFields.note}
                onChange={(e) => setConfirmFields((f) => ({ ...f, note: e.target.checked }))}
              />
            </div>
          </CCardBody>
        </CCard>
      )}
    </>
  )

  const renderNotifications = () => (
    <>
      <CCard className="border-secondary-subtle rounded-4 mb-4">
        <CCardBody>
          <div className="fw-semibold fs-6 mb-1">Approval Requests</div>
          <div className="small mb-3">
            Receive notifications when a member submits information requiring manual approval.
          </div>
          {hideApprovalNotifications ? (
            <InfoBox color="light" icon={cilInfo}>
              Approval-request notifications are unavailable because this game automatically
              approves verification submissions.
            </InfoBox>
          ) : (
            <CRow className="g-3">
              <CCol md={4}>
                <SelectableCard
                  indicator="radio"
                  icon={cilBell}
                  title="Off"
                  description="Do not send approval emails"
                  selected={approvalNotificationType === 'off'}
                  onClick={() => setApprovalNotificationType('off')}
                />
              </CCol>
              <CCol md={4}>
                <SelectableCard
                  indicator="radio"
                  icon={cilEnvelopeClosed}
                  title="Instant Email"
                  description="Receive an email every time a member submits their requirement."
                  selected={approvalNotificationType === 'instant'}
                  onClick={() => setApprovalNotificationType('instant')}
                />
              </CCol>
              <CCol md={4}>
                <SelectableCard
                  indicator="radio"
                  icon={cilCalendarCheck}
                  title="Daily Summary"
                  description="Receive one summary email each day with all new requests."
                  selected={approvalNotificationType === 'daily'}
                  onClick={() => setApprovalNotificationType('daily')}
                />
              </CCol>
            </CRow>
          )}
        </CCardBody>
      </CCard>

      <CCard className="border-secondary-subtle rounded-4">
        <CCardBody>
          <div className="fw-semibold fs-6 mb-1">New Members Joined</div>
          <div className="small mb-3">
            Receive notifications when a new member successfully joins this game.
          </div>
          <CRow className="g-3">
            <CCol md={4}>
              <SelectableCard
                indicator="radio"
                icon={cilBell}
                title="Off"
                description="Do not send signup emails"
                selected={newMemberNotificationType === 'off'}
                onClick={() => setNewMemberNotificationType('off')}
              />
            </CCol>
            <CCol md={4}>
              <SelectableCard
                indicator="radio"
                icon={cilEnvelopeClosed}
                title="Instant Email"
                description="Receive an email whenever a member joins."
                selected={newMemberNotificationType === 'instant'}
                onClick={() => setNewMemberNotificationType('instant')}
              />
            </CCol>
            <CCol md={4}>
              <SelectableCard
                indicator="radio"
                icon={cilCalendarCheck}
                title="Daily Summary"
                description="Receive one summary email each day with all new members."
                selected={newMemberNotificationType === 'daily'}
                onClick={() => setNewMemberNotificationType('daily')}
              />
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </>
  )

  const stepRenderers = {
    1: renderVisibility,
    2: renderNotifications,
    3: renderInvitation,
  }

  return (
    <>
      <StepperBar activeStepKey="game_entry" />
      {/* <div className="ge-page-header">
        <CCard className="border-0 shadow-sm">
          <CCardBody className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 bg-primary-subtle text-primary flex-shrink-0"
              style={{ width: 44, height: 44 }}
            >
              <CIcon icon={cilCog} size="xl" />
            </div>
            <div>
              <h1 className="ge-page-title">Game Entry</h1>
              <p className="ge-page-subtitle">Control how members can access and join this game.</p>
            </div>
          </CCardBody>
        </CCard>
      </div> */}

      <StepperNav steps={stepMeta} current={step} onStepClick={goToStep} />

      <CRow className="g-4">
        <CCol lg={8}>
          <CCard className="mb-4 border-0 shadow-sm">
            <CCardHeader className="bg-white border-bottom">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 bg-primary-subtle text-primary flex-shrink-0"
                  style={{ width: 44, height: 44 }}
                >
                  <CIcon icon={STEP_ICONS[step - 1]} size="xl" />
                </div>
                <div>
                  <div className="fw-semibold fs-5 d-flex align-items-center gap-2">
                    {SECTION_TITLES[step - 1]}
                    {step === 2 && (
                      <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis small">
                        Optional
                      </span>
                    )}
                  </div>
                  <div className="small">{sectionSubtitles[step - 1]}</div>
                </div>
              </div>
            </CCardHeader>
            <CCardBody className="pt-4">{stepRenderers[step]()}</CCardBody>
          </CCard>

          <div className="d-flex justify-content-between align-items-center pt-2">
            <CButton
              color="secondary"
              variant="outline"
              disabled={step === 1 || saving}
              onClick={goBack}
            >
              <CIcon icon={cilArrowLeft} className="me-1" />
              Back
            </CButton>
            <div className="d-flex flex-column align-items-end gap-1">
              <div className="d-flex gap-2">
                {/* <CButton
                  color="secondary"
                  variant="outline"
                  disabled={saving}
                  onClick={() => navigate('/game-entry')}
                >
                  Cancel
                </CButton> */}
                {activeSteps.indexOf(step) !== activeSteps.length - 1 && (
                  <>
                    <CButton
                      color="primary"
                      variant="outline"
                      className="shadow-sm"
                      disabled={saving || (step === 1 && isTeamExists)}
                      onClick={handleUpdate}
                    >
                      {saving ? (
                        'Updating...'
                      ) : (
                        <>
                          <CIcon icon={cilCheck} className="me-1" />
                          Update
                        </>
                      )}
                    </CButton>
                    <CButton
                      color="primary"
                      className="shadow-sm"
                      disabled={saving}
                      onClick={handleNext}
                    >
                      Next →
                    </CButton>
                  </>
                )}
              </div>
              {step === 1 && isTeamExists && (
                <div className="small text-medium-emphasis">
                  Entry settings can&rsquo;t be changed once a team has joined this game.
                </div>
              )}
            </div>
          </div>
        </CCol>

        <CCol lg={4}>
          {step === 3 && invitationTab === 'import_guest' && importSummary && (
            <CCard className="mb-4 border-0 shadow-sm">
              <CCardHeader className="bg-white border-bottom">
                <div className="fw-semibold">Import Summary</div>
              </CCardHeader>
              <CCardBody>
                {[
                  { label: 'File rows', value: importSummary.fileRows, icon: cilDescription },
                  { label: 'Ready to import', value: importSummary.ready, icon: cilCheckCircle },
                  { label: 'Excluded', value: importSummary.excluded, icon: cilWarning },
                  {
                    label: 'Needs correction',
                    value: importSummary.needsCorrection,
                    icon: cilWarning,
                  },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="d-flex align-items-center justify-content-between py-2 border-bottom"
                  >
                    <div className="d-flex align-items-center gap-2 text-medium-emphasis">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle bg-body-secondary flex-shrink-0"
                        style={{ width: 28, height: 28 }}
                      >
                        <CIcon icon={row.icon} size="sm" />
                      </div>
                      <span>{row.label}</span>
                    </div>
                    <span className="fw-semibold">{row.value}</span>
                  </div>
                ))}
                <InfoBox color="light" icon={cilInfo} className="mt-3 mb-0">
                  No emails will be sent during import. You can review all recipients before sending
                  invitations.
                </InfoBox>
              </CCardBody>
            </CCard>
          )}

          {step === 3 && invitationTab === 'create_list' && (
            <CCard className="mb-4 border-0 shadow-sm">
              <CCardHeader className="bg-white border-bottom">
                <div className="fw-semibold">Invitation Summary</div>
              </CCardHeader>
              <CCardBody>
                {[
                  {
                    label: 'Game access',
                    value: visibilityLabel,
                    icon: visibility === 'private' ? cilLockLocked : cilGlobeAlt,
                  },
                  { label: 'Recipients added', value: addPeopleCount ?? 0, icon: cilPeople },
                  { label: 'Game Codes', value: 'Created when sent', icon: cilCreditCard },
                  { label: 'Approval', value: 'Automatically Approved', icon: cilBolt },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="d-flex align-items-center justify-content-between py-2 border-bottom"
                  >
                    <div className="d-flex align-items-center gap-2 text-medium-emphasis">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle bg-body-secondary flex-shrink-0"
                        style={{ width: 28, height: 28 }}
                      >
                        <CIcon icon={row.icon} size="sm" />
                      </div>
                      <span>{row.label}</span>
                    </div>
                    <span className="fw-semibold">{row.value}</span>
                  </div>
                ))}
              </CCardBody>
            </CCard>
          )}

          {step === 3 && invitationTab === 'member_invite' && (
            <CCard className="mb-4 border-0 shadow-sm">
              <CCardHeader className="bg-white border-bottom">
                <div className="fw-semibold">Invitation Summary</div>
              </CCardHeader>
              <CCardBody>
                {[
                  {
                    label: 'Game visibility',
                    value: visibilityLabel,
                    icon: visibility === 'private' ? cilLockLocked : cilGlobeAlt,
                  },
                  { label: 'Join Access', value: 'Open', icon: cilLink },
                  { label: 'Approval', value: 'Automatically Approved', icon: cilBolt },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="d-flex align-items-center justify-content-between py-2 border-bottom"
                  >
                    <div className="d-flex align-items-center gap-2 text-medium-emphasis">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle bg-body-secondary flex-shrink-0"
                        style={{ width: 28, height: 28 }}
                      >
                        <CIcon icon={row.icon} size="sm" />
                      </div>
                      <span>{row.label}</span>
                    </div>
                    <span className="fw-semibold">{row.value}</span>
                  </div>
                ))}
              </CCardBody>
            </CCard>
          )}

          <CCard className="mb-4 border-0 shadow-sm">
            <CCardHeader className="bg-white border-bottom">
              <div className="fw-semibold">Game Entry Summary</div>
              <div className="small">Here&rsquo;s how members will join this game.</div>
            </CCardHeader>
            <CCardBody>
              {summaryRows.map((row) => (
                <div
                  key={row.label}
                  className="d-flex align-items-center justify-content-between py-2 border-bottom"
                >
                  <div className="d-flex align-items-center gap-2 text-medium-emphasis">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-body-secondary flex-shrink-0"
                      style={{ width: 28, height: 28 }}
                    >
                      <CIcon icon={row.icon} size="sm" />
                    </div>
                    <span>{row.label}</span>
                  </div>
                  <span className={row.value === '—' ? 'text-medium-emphasis' : 'fw-semibold'}>
                    {row.value}
                  </span>
                </div>
              ))}
            </CCardBody>
          </CCard>

          <CCard className="border-0 shadow-sm">
            <CCardHeader className="bg-primary-subtle border-0">
              <div className="fw-semibold text-primary">Member Journey (Preview)</div>
              <div className="small">This is how members will experience joining this game.</div>
            </CCardHeader>
            <CCardBody>
              <div className="position-relative">
                {journeySteps.map((j, idx) => (
                  <div key={j.title} className="d-flex gap-3 mb-3 position-relative">
                    {idx < journeySteps.length - 1 && (
                      <div
                        className="position-absolute bg-primary-subtle"
                        style={{ left: 13, top: 28, bottom: -12, width: 2 }}
                      />
                    )}
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary fw-semibold flex-shrink-0"
                      style={{ width: 28, height: 28, fontSize: 13, zIndex: 1 }}
                    >
                      {idx + 1}
                    </div>
                    <div>
                      <div className="fw-semibold small">{j.title}</div>
                      <div className="small">{j.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="small mt-2">
                You can edit these settings anytime from Game Entry Settings.
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </>
  )
}

export default GameEntrySettings
